import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Checkbox,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Flex,
  Input,
  Spacer,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as yup from 'yup';
import Router from 'next/router';
import withLogger, { Logger } from '../libs/logger';
import { EventStateContext } from '../context/EventContext';
import Storyblok from '../libs/storyblok';

type LoginFormProps = {
  callback(err: string | null, user?: { email: string; fName: string; lName }): void;
  log: Logger;
};

function CredentialsForm({ callback, log }: LoginFormProps) {
  const [error, setError] = useState('');
  const { slug } = useContext(EventStateContext);
  const [showSupport, setShowSupport] = useState(false);

  const errorMessage = error.length > 0 && (
    <Alert status='error'>
      <AlertIcon />
      {error}
    </Alert>
  );

  useEffect(() => {
    if (!slug) {
      return;
    }

    Storyblok.get(`cdn/stories/${slug.replace('conference-settings', '')}support`, {})
      .then(({ data }: any) => {
        setShowSupport(true);
      })
      .catch((err) => {
        setShowSupport(false);
        console.error('storyblok.get error', err, { name: err.name, message: err.message });
      });
  }, [slug]);

  function addCredentials(evt: any) {
    setError('');
    const { email, fName, lName } = evt;
    return callback(null, { email, fName, lName });
  }

  const loginSchema = yup.object({
    email: yup.string().email('Must be a valid email').required('Email is required'),
    fName: yup.string().required('First name is required'),
    lName: yup.string().required('Last name is required'),
    terms: yup
      .bool()
      .required('The terms and privacy policy must be accepted.')
      .oneOf([true], 'The terms and privacy policy must be accepted.')
  });

  return (
    <Box>
      <Formik
        validationSchema={loginSchema}
        onSubmit={addCredentials}
        initialValues={{ email: '', fName: '', lName: '' }}
      >
        {({
          isSubmitting,
          handleSubmit,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          values,
          touched,
          errors
        }) => (
          <Form noValidate onSubmit={handleSubmit} color='gray.400'>
            {errorMessage}
            <Field name='fName'>
              {({ field, form }) => (
                <FormControl mb='4' isInvalid={form.errors.fName && form.touched.fName}>
                  <FormLabel htmlFor='fName' srOnly>
                    First name
                  </FormLabel>
                  <Input
                    {...field}
                    id='fName'
                    placeholder='First name'
                    bg={useColorModeValue('gray.300', 'whiteAlpha.400')}
                    colorScheme='whiteAlpha'
                    _placeholder={{ color: useColorModeValue('blackAlpha.500', 'whiteAlpha.700') }}
                  />
                  <FormErrorMessage>{form.errors.fName}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Field name='lName'>
              {({ field, form }) => (
                <FormControl mb='4' isInvalid={form.errors.lName && form.touched.lName}>
                  <FormLabel htmlFor='lName' srOnly>
                    Last name
                  </FormLabel>
                  <Input
                    {...field}
                    id='lName'
                    placeholder='Last name'
                    bg={useColorModeValue('gray.300', 'whiteAlpha.400')}
                    colorScheme='whiteAlpha'
                    _placeholder={{ color: useColorModeValue('blackAlpha.500', 'whiteAlpha.700') }}
                  />
                  <FormErrorMessage>{form.errors.lName}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Field name='email'>
              {({ field, form }) => (
                <FormControl mb='4' isInvalid={form.errors.email && form.touched.email}>
                  <FormLabel htmlFor='email' srOnly>
                    Email
                  </FormLabel>
                  <Input
                    {...field}
                    id='email'
                    placeholder='Email'
                    bg={useColorModeValue('gray.300', 'whiteAlpha.400')}
                    colorScheme='whiteAlpha'
                    _placeholder={{ color: useColorModeValue('blackAlpha.500', 'whiteAlpha.700') }}
                  />
                  <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Flex mb='8'>
              <Spacer />
              <Field name='terms'>
                {({ field, form }) => (
                  <FormControl isInvalid={form.errors.terms && form.touched.terms}>
                    <Flex justifyContent='space-between'>
                      <Checkbox
                        {...field}
                        id='terms'
                        isInvalid={errors.terms}
                        size='lg'
                        colorScheme='blue'
                        borderColor={useColorModeValue('blackAlpha.700', 'whiteAlpha.700')}
                      />
                      <Box ml='8' pl='5' textAlign='right'>
                        Accept&nbsp;
                        <Button
                          variant='link'
                          colorScheme='whiteAlpha'
                          color={useColorModeValue('blackAlpha.600', 'whiteAlpha.600')}
                        >
                          <a href='/docs/Digital_Access_Terms_and_Conditions_EU.pdf' target='_blank' rel='noreferrer'>
                            terms
                          </a>
                        </Button>
                        &nbsp;and&nbsp;
                        <Button
                          variant='link'
                          colorScheme='whiteAlpha'
                          color={useColorModeValue('blackAlpha.600', 'whiteAlpha.600')}
                        >
                          <a href='/docs/Digital_Access_DATA PROTECTION POLICY_EU.pdf' target='_blank' rel='noreferrer'>
                            privacy policy
                          </a>
                        </Button>
                        .
                      </Box>
                    </Flex>
                    <Text fontSize='smaller' color='red.500'>
                      {errors.terms}
                    </Text>
                  </FormControl>
                )}
              </Field>
            </Flex>
            <Center>
              <Button width='100%' type='submit' isLoading={isSubmitting} colorScheme='brand'>
                Continue
              </Button>
            </Center>
            {showSupport && (
              <Flex mt='8'>
                <Spacer />
                <Button
                  variant='link'
                  colorScheme='brand'
                  onClick={() => {
                    Router.push(`${slug}support`);
                  }}
                >
                  Need Support?
                </Button>
              </Flex>
            )}
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default withLogger(CredentialsForm, 'component:CredentialsForm');
